/**
 * Support page reached from the sidebar "Behöver du hjälp?" card.
 * Plain FAQ + support email. No live data.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ChevronDown, Mail } from 'lucide-react';
import { usePageTitle } from '../use-page-title.js';

const HB = '#1A3D2B';
const SUPPORT_EMAIL = 'support@regta.se';
const TERMS_URL = 'https://regta.se/allmanna-villkor';
const PRIVACY_URL = 'https://regta.se/integritetsskyddspolicy';

type FaqItem = {
  q: string;
  a: string;
};

const faq: FaqItem[] = [
  {
    q: 'Hur skapar jag ett nytt avtal?',
    a:
      'Gå till Mallar, välj den mall som passar (bostadshyra, lokalhyra, indexavtal m.fl.) och klicka för att skapa ett nytt avtal. Avtalet öppnas i editorn där fält som redan finns i Regta autofylls direkt.',
  },
  {
    q: 'Vilka avtalsmallar finns?',
    a:
      'Regta innehåller juridiskt granskade svenska hyresavtal — bland annat bostadshyra, lokalhyra, indexavtal och förhandlingsordning. Mallbiblioteket uppdateras centralt så du alltid jobbar med senaste versionen.',
  },
  {
    q: 'Kan jag importera befintliga avtal?',
    a:
      'Ja. Under Importera kan du ladda upp PDF eller Word-filer — enstaka avtal eller hela bestånd i bulk. Regta strukturerar innehållet automatiskt och du kan justera fält innan avtalet sparas.',
  },
  {
    q: 'Hur fungerar granskning innan signering?',
    a:
      'Skicka ett avtal på intern granskning till en kollega innan det går ut. Granskaren ser status och kan kommentera. Pågående granskningar listas under Granskningar och visas som badge i sidomenyn.',
  },
  {
    q: 'Hur fungerar digital signering?',
    a:
      'Avtalet skickas till parterna för digital signering via Scrive. Status uppdateras automatiskt när någon signerar, och det färdiga signerade avtalet sparas i Regta tillsammans med signeringsspåret.',
  },
  {
    q: 'När får jag påminnelser om viktiga datum?',
    a:
      'Regta bevakar förhandlingsordning, uppsägning och lokalavtal. Påminnelser skickas automatiskt 3 månader, 1 månad och 1 vecka före nyckeldatum — så inget missas.',
  },
  {
    q: 'Vad är grupper och hur används de?',
    a:
      'Grupper avgränsar vilka avtal, fastigheter och motparter en användare ser. Administratörer hanterar grupper under Hantering → Grupper och tilldelar användare till en eller flera grupper.',
  },
  {
    q: 'Hur bjuder jag in nya användare?',
    a:
      'Under Hantering → Användare kan administratörer bjuda in nya användare via e-post och tilldela roll samt grupp. Den inbjudna får ett mejl med en länk för att logga in.',
  },
  {
    q: 'Var ändrar jag organisationens uppgifter?',
    a:
      'Inställningar (kugghjulet i sidomenyn) — där hanteras organisationsuppgifter, profil och annan kontoinformation.',
  },
  {
    q: 'Jag hittar inte svaret på min fråga.',
    a:
      `Mejla oss på ${SUPPORT_EMAIL} så återkommer vi så snart vi kan. Beskriv gärna vad du försökte göra och vilket avtal eller mall det gäller.`,
  },
];

export function SupportPage() {
  usePageTitle('Support');

  return (
    <article className="max-w-3xl space-y-12">
      <div>
        <Link
          to="/"
          className="inline-flex items-center gap-1 text-xs uppercase tracking-widest text-stone-400 hover:text-stone-600"
        >
          <ArrowLeft className="h-3 w-3" />
          Tillbaka till översikten
        </Link>
        <h1 className="font-display text-3xl font-light tracking-tight mt-4 text-stone-900">
          Support
        </h1>
        <p className="text-sm text-stone-500 leading-relaxed mt-3 max-w-xl">
          Vanliga frågor om hur Regta fungerar. Hittar du inte svaret är du
          alltid välkommen att höra av dig direkt.
        </p>
      </div>

      <section className="border border-stone-200 p-6 flex items-start gap-4">
        <div
          className="flex h-10 w-10 shrink-0 items-center justify-center text-white"
          style={{ backgroundColor: HB }}
        >
          <Mail className="h-4 w-4" />
        </div>
        <div className="min-w-0">
          <h2 className="font-display text-lg font-light text-stone-900">
            Kontakta oss
          </h2>
          <p className="text-sm text-stone-500 leading-relaxed mt-1">
            Mejla supporten så hjälper vi dig vidare.
          </p>
          <a
            href={`mailto:${SUPPORT_EMAIL}`}
            className="inline-block mt-2 text-sm hover:underline"
            style={{ color: HB }}
          >
            {SUPPORT_EMAIL}
          </a>
        </div>
      </section>

      <section>
        <h2 className="font-display text-xl font-light text-stone-900">
          Vanliga frågor
        </h2>
        <ul className="mt-4 divide-y divide-stone-200 border-y border-stone-200">
          {faq.map((item, i) => (
            <FaqRow key={i} item={item} />
          ))}
        </ul>
      </section>

      <section>
        <h2 className="font-display text-xl font-light text-stone-900">
          Juridik
        </h2>
        <ul className="mt-4 flex flex-col gap-2">
          <li>
            <a
              href={TERMS_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm hover:underline"
              style={{ color: HB }}
            >
              Allmänna villkor
            </a>
          </li>
          <li>
            <a
              href={PRIVACY_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm hover:underline"
              style={{ color: HB }}
            >
              Integritetsskyddspolicy
            </a>
          </li>
        </ul>
      </section>

      <section className="border-t border-stone-200 pt-8">
        <p className="text-sm text-stone-500 leading-relaxed max-w-xl">
          Regta utvecklar ständigt sina avtalsmallar och den tekniska driften
          bakom dessa. Har du märkt av ett fel eller har andra synpunkter för att
          göra vår tjänst bättre är du välkommen att höra av dig till oss på{' '}
          <a
            href={`mailto:${SUPPORT_EMAIL}`}
            className="hover:underline"
            style={{ color: HB }}
          >
            {SUPPORT_EMAIL}
          </a>
          .
        </p>
      </section>
    </article>
  );
}

function FaqRow({ item }: { item: FaqItem }) {
  const [open, setOpen] = useState(false);
  return (
    <li>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full items-center justify-between gap-4 py-4 text-left"
      >
        <span className="text-sm font-medium text-stone-900">{item.q}</span>
        <ChevronDown
          className={[
            'h-4 w-4 shrink-0 text-stone-400 transition-transform',
            open ? 'rotate-180' : '',
          ].join(' ')}
        />
      </button>
      {open && (
        <p className="pb-4 pr-8 text-sm text-stone-500 leading-relaxed">
          {item.a}
        </p>
      )}
    </li>
  );
}
